import { useEffect, useId, useMemo, useState, type ReactElement } from 'react';
import { Archive, Copy, MoveRight, Split, X } from 'lucide-react';
import { Button, Dialog, IconButton, Notice } from '@/components';
import {
  cardMoveNeighbours,
  type CardMovePlacement,
  type DestinationBoardOption,
  type DestinationListOption,
} from '@/lib/cross-board-move';
import { cardDisplayTitle } from '@/lib/card-title';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';

export type BulkCardActionMode = 'archive' | 'move' | 'copy' | 'merge';

export interface BulkCardActionSubmit {
  readonly mode: BulkCardActionMode;
  readonly boardId: string;
  readonly listId: string;
  readonly previousCardId: string | null;
  readonly nextCardId: string | null;
  readonly keepChecklists: boolean;
  readonly keepLabels: boolean;
  readonly keepMembers: boolean;
  readonly keepAttachments: boolean;
  readonly keepDates: boolean;
  readonly destinationListTitle?: string | undefined;
  readonly targetCardId?: string | undefined;
  readonly sourceCardId?: string | undefined;
  readonly targetTitle?: string | undefined;
  readonly sourceTitle?: string | undefined;
}

export interface BulkCardActionsDialogProps {
  readonly open: boolean;
  readonly initialMode: BulkCardActionMode;
  readonly selectedCount: number;
  readonly selectedCards?: readonly { id: string; title: string }[] | undefined;
  readonly currentBoardId: string;
  readonly boards: readonly DestinationBoardOption[];
  readonly listsByBoardId: ReadonlyMap<string, readonly DestinationListOption[]>;
  readonly cardsByListId: ReadonlyMap<string, readonly { id: string; title: string }[]>;
  readonly loading?: boolean | undefined;
  readonly busy?: boolean | undefined;
  readonly error?: string | null | undefined;
  readonly onCancel: () => void;
  readonly onSubmit: (input: BulkCardActionSubmit) => void;
}

export function BulkCardActionsDialog({
  open,
  initialMode,
  selectedCount,
  selectedCards = [],
  currentBoardId,
  boards,
  listsByBoardId,
  cardsByListId,
  loading,
  busy,
  error,
  onCancel,
  onSubmit,
}: BulkCardActionsDialogProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const boardSelectId = useId();
  const listSelectId = useId();
  const positionSelectId = useId();
  const [mode, setMode] = useState<BulkCardActionMode>(initialMode);
  const [selectedBoardId, setSelectedBoardId] = useState('');
  const [selectedListId, setSelectedListId] = useState('');
  const [placement, setPlacement] = useState<CardMovePlacement>('bottom');
  const [keepChecklists, setKeepChecklists] = useState(true);
  const [keepLabels, setKeepLabels] = useState(true);
  const [keepMembers, setKeepMembers] = useState(true);
  const [keepAttachments, setKeepAttachments] = useState(true);
  const [keepDates, setKeepDates] = useState(true);

  const defaultBoardId = useMemo(() => {
    if (boards.some((board) => board.id === currentBoardId)) return currentBoardId;
    return boards[0]?.id ?? '';
  }, [boards, currentBoardId]);

  const selectedBoard =
    boards.find((board) => board.id === selectedBoardId) ??
    boards.find((board) => board.id === defaultBoardId) ??
    null;
  const availableLists =
    selectedBoard === null ? [] : (listsByBoardId.get(selectedBoard.id) ?? []);
  const effectiveListId = availableLists.some((list) => list.id === selectedListId)
    ? selectedListId
    : (availableLists[0]?.id ?? '');
  const selectedList = availableLists.find((list) => list.id === effectiveListId) ?? null;
  const positionCards = cardsByListId.get(effectiveListId) ?? [];
  const placementChoices = useMemo(
    () => bulkPlacementChoices(t, positionCards),
    [positionCards, t],
  );

  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setSelectedBoardId(defaultBoardId);
    setSelectedListId('');
    setPlacement('bottom');
    setKeepChecklists(true);
    setKeepLabels(true);
    setKeepMembers(true);
    setKeepAttachments(true);
    setKeepDates(true);
  }, [defaultBoardId, initialMode, open]);

  if (!open) return null;

  const needsDestination = mode === 'move' || mode === 'copy';
  const mergeCards = selectedCards.slice(0, 2);
  const confirmDisabled =
    busy === true ||
    selectedCount === 0 ||
    (mode === 'merge' && selectedCount !== 2) ||
    (needsDestination &&
      (loading === true || selectedBoard === null || selectedList === null));
  const disabledReason =
    selectedCount === 0
      ? t('copyMove.bulk.needsSelection')
      : mode === 'merge' && selectedCount !== 2
        ? t('copyMove.bulk.mergeNeedsTwo')
        : loading === true && needsDestination
          ? t('copyMove.loadingBoards')
          : needsDestination && selectedBoard === null
            ? t('copyMove.bulk.needsBoard')
            : needsDestination && selectedList === null
              ? t('copyMove.bulk.needsList')
              : undefined;
  const busyProps = busy === undefined ? {} : { busy };
  const disabledReasonProps = disabledReason === undefined ? {} : { disabledReason };
  /* ⚠ FOUR LITERAL KEYS IN AN EXPLICIT TERNARY, never
     `t('copyMove.bulk.action.' + mode, …)`. A concatenated key type-checks and
     renders correctly and hides all four keys from the `t('…')` anchor the
     routing pin counts and orphan-checks with. The count is passed twice at
     every arm — into `values` for the slot, and as the arm selector. */
  const count = { count: selectedCount };
  const actionTitle =
    mode === 'archive'
      ? t('copyMove.bulk.action.archive', count, selectedCount)
      : mode === 'move'
        ? t('copyMove.bulk.action.move', count, selectedCount)
        : mode === 'copy'
          ? t('copyMove.bulk.action.copy', count, selectedCount)
          : t('copyMove.bulk.action.merge', count, selectedCount);

  function submit() {
    if (confirmDisabled) return;

    if (mode === 'archive') {
      onSubmit({
        mode,
        boardId: currentBoardId,
        listId: '',
        previousCardId: null,
        nextCardId: null,
        keepChecklists,
        keepLabels,
        keepMembers,
        keepAttachments,
        keepDates,
      });
      return;
    }

    if (mode === 'merge') {
      const [target, source] = mergeCards;
      if (target === undefined || source === undefined) return;
      onSubmit({
        mode,
        boardId: currentBoardId,
        listId: '',
        previousCardId: null,
        nextCardId: null,
        keepChecklists,
        keepLabels,
        keepMembers,
        keepAttachments,
        keepDates,
        targetCardId: target.id,
        sourceCardId: source.id,
        targetTitle: printableCardTitle(t, target.title),
        sourceTitle: printableCardTitle(t, source.title),
      });
      return;
    }

    if (selectedBoard === null || selectedList === null) return;
    const neighbours = cardMoveNeighbours(positionCards, placement);
    onSubmit({
      mode,
      boardId: selectedBoard.id,
      listId: selectedList.id,
      previousCardId: neighbours.previousCardId,
      nextCardId: neighbours.nextCardId,
      keepChecklists,
      keepLabels,
      keepMembers,
      keepAttachments,
      keepDates,
      destinationListTitle: selectedList.title,
    });
  }

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      size="md"
      title={t('copyMove.bulk.title')}
      description={t('copyMove.bulk.selectedCount', count, selectedCount)}
      closeControl={
        <IconButton label={t('copyMove.bulk.close')} size="sm" variant="quiet">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onCancel}>
            {t('copyMove.cancel')}
          </Button>
          <Button
            size="sm"
            variant={mode === 'archive' ? 'danger' : 'primary'}
            iconStart={
              mode === 'archive' ? (
                <Archive aria-hidden="true" className="size-4" />
              ) : mode === 'move' ? (
                <MoveRight aria-hidden="true" className="size-4" />
              ) : mode === 'copy' ? (
                <Copy aria-hidden="true" className="size-4" />
              ) : (
                <Split aria-hidden="true" className="size-4" />
              )
            }
            disabled={confirmDisabled}
            onClick={submit}
            {...busyProps}
            {...disabledReasonProps}
          >
            {actionTitle}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <Notice tone="bad" title={t('copyMove.bulk.errorTitle')} body={error} />
        ) : null}

        <div className="grid grid-cols-3 gap-2">
          {(['archive', 'move', 'copy', 'merge'] as const).map((nextMode) => (
            <Button
              key={nextMode}
              type="button"
              size="sm"
              variant={mode === nextMode ? 'primary' : 'secondary'}
              onClick={() => setMode(nextMode)}
            >
              {nextMode === 'archive'
                ? t('copyMove.bulk.mode.archive')
                : nextMode === 'move'
                  ? t('copyMove.bulk.mode.move')
                  : nextMode === 'copy'
                    ? t('copyMove.bulk.mode.copy')
                    : t('copyMove.bulk.mode.merge')}
            </Button>
          ))}
        </div>

        <Notice
          tone={mode === 'archive' || mode === 'merge' ? 'warn' : 'info'}
          title={actionTitle}
          body={
            mode === 'archive'
              ? t('copyMove.bulk.archiveBody')
              : mode === 'merge'
                ? selectedCount !== 2
                  ? t('copyMove.bulk.mergeNeedsTwo')
                  : t('copyMove.bulk.mergeBody', {
                      target: printableCardTitle(t, mergeCards[0]?.title),
                      source: printableCardTitle(t, mergeCards[1]?.title),
                    })
                : selectedList === null
                  ? t('copyMove.bulk.chooseDestination')
                  : /* ⚠ TWO WHOLE SENTENCES, not one with a `{verb}` slot. The
                       source interpolated the PARTICIPLE, which no catalogue
                       can translate: agreement, word order and the
                       participle's own form are all language-specific. */
                    mode === 'move'
                    ? t('copyMove.bulk.willMove', { list: selectedList.title })
                    : t('copyMove.bulk.willCopy', { list: selectedList.title })
          }
        />

        {needsDestination ? (
          <div className="grid gap-3 md:grid-cols-3">
            <label className="flex flex-col gap-1 text-sm font-medium text-ink">
              <span id={boardSelectId}>{t('copyMove.bulk.boardLabel')}</span>
              <select
                aria-labelledby={boardSelectId}
                value={selectedBoard?.id ?? ''}
                onChange={(event) => {
                  setSelectedBoardId(event.currentTarget.value);
                  setSelectedListId('');
                  setPlacement('bottom');
                }}
                className="rounded-card border border-border-strong bg-surface px-2 py-2 text-sm text-ink"
              >
                {boards.map((board) => (
                  <option key={board.id} value={board.id}>
                    {board.title}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-sm font-medium text-ink">
              <span id={listSelectId}>{t('copyMove.bulk.listLabel')}</span>
              <select
                aria-labelledby={listSelectId}
                value={effectiveListId}
                onChange={(event) => {
                  setSelectedListId(event.currentTarget.value);
                  setPlacement('bottom');
                }}
                className="rounded-card border border-border-strong bg-surface px-2 py-2 text-sm text-ink"
              >
                {availableLists.map((list) => (
                  <option key={list.id} value={list.id}>
                    {list.title}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-sm font-medium text-ink">
              <span id={positionSelectId}>{t('copyMove.positionLabel')}</span>
              <select
                aria-labelledby={positionSelectId}
                value={placement}
                onChange={(event) =>
                  setPlacement(event.currentTarget.value as CardMovePlacement)
                }
                className="rounded-card border border-border-strong bg-surface px-2 py-2 text-sm text-ink"
              >
                {placementChoices.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : null}

        {mode === 'copy' ? (
          <fieldset className="grid gap-2 rounded-card border border-border bg-surface-raised p-3 text-sm text-ink">
            <legend className="px-1 font-medium">{t('copyMove.bulk.keepLegend')}</legend>
            {/* ⚠ Each label is a LITERAL `t('…')` in place. The array is not
                rewritten to hold keys resolved later, for the same reason the
                visibility choices in `BoardCopyDialog` are not: a key that
                reaches `t` through a variable is invisible to the anchor. */}
            {[
              [
                'keepChecklists',
                t('copyMove.bulk.keep.checklists'),
                keepChecklists,
                setKeepChecklists,
              ],
              ['keepLabels', t('copyMove.bulk.keep.labels'), keepLabels, setKeepLabels],
              [
                'keepMembers',
                t('copyMove.bulk.keep.members'),
                keepMembers,
                setKeepMembers,
              ],
              [
                'keepAttachments',
                t('copyMove.bulk.keep.attachments'),
                keepAttachments,
                setKeepAttachments,
              ],
              ['keepDates', t('copyMove.bulk.keep.dates'), keepDates, setKeepDates],
            ].map(([id, label, checked, setChecked]) => (
              <label key={id as string} className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={checked as boolean}
                  onChange={(event) =>
                    (setChecked as (next: boolean) => void)(event.currentTarget.checked)
                  }
                />
                {label as string}
              </label>
            ))}
          </fieldset>
        ) : null}
      </div>
    </Dialog>
  );
}

/** ⚠ `t` first and required — a module helper cannot call the hook. */
function bulkPlacementChoices(
  t: InterfaceLanguageContextValue['t'],
  cards: readonly { id: string; title: string }[],
): { value: CardMovePlacement; label: string }[] {
  return [
    { value: 'top', label: t('copyMove.position.topOfList') },
    ...cards.map((card) => ({
      value: `after:${card.id}` as const,
      label: t('copyMove.position.afterCard', {
        card: cardDisplayTitle(t, card.title),
      }),
    })),
    { value: 'bottom', label: t('copyMove.position.bottomOfList') },
  ];
}

/**
 * ⚠ THE ONE PLACE `cardDisplayTitle` MUST NOT BE USED, and it is not a style
 * question. Two measured reasons:
 *
 * 1. The leaf trims differently ON PURPOSE — it returns the ORIGINAL string
 *    when it is not blank, where this helper returns the TRIMMED one, so a card
 *    called `'  Ship it  '` would change value.
 * 2. This value leaves the file on a MUTATION PAYLOAD (`targetTitle` /
 *    `sourceTitle`, which `BoardFace` forwards into `mergeCards.mutate`). That
 *    is a wire value, not a DOM node.
 *
 * So the KEY is reused and the bytes are never re-authored, while the trim
 * behaviour stays byte-identical to what shipped.
 */
function printableCardTitle(
  t: InterfaceLanguageContextValue['t'],
  title: string | undefined,
): string {
  const trimmed = title?.trim();
  return trimmed === undefined || trimmed.length === 0
    ? t('board.card.untitled')
    : trimmed;
}
